import { useState, useCallback } from 'react';
import * as subscriptionService from '../services/subscriptionService';
import { ApiResponse } from '../services/types';
import { ISubscriptionPlan } from '../types/interfaces/ISubscription';
import { IPaymentMethod } from '../types/interfaces/IPaymentMethod';
import { getApiErrorMessage } from '../services/core/apiResponseHelpers';

// Hook unificado para suscripciones, planes y métodos de pago
export function useSubscription() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ===== SUSCRIPCIONES =====
  const getCurrentSubscription = useCallback(async (userId: string) => {
    return await subscriptionService.getCurrentSubscription(userId);
  }, []);

  const getPaymentHistory = useCallback(async (userId: string) => {
    return await subscriptionService.getPaymentHistory(userId);
  }, []);

  const createSubscription = useCallback(
    async (userId: string, subscriptionData: any) => {
      return await subscriptionService.createSubscription(
        userId,
        subscriptionData
      );
    },
    []
  );

  const processPayment = useCallback(
    async (subscriptionId: string, paymentData: any) => {
      return await subscriptionService.processPayment(
        subscriptionId,
        paymentData
      );
    },
    []
  );

  const cancelSubscription = useCallback(async (subscriptionId: string) => {
    return await subscriptionService.cancelSubscription(subscriptionId);
  }, []);

  const reactivateSubscription = useCallback(async (subscriptionId: string) => {
    return await subscriptionService.reactivateSubscription(subscriptionId);
  }, []);

  const changePlan = useCallback(
    async (subscriptionId: string, planId: string) => {
      return await subscriptionService.changePlan(subscriptionId, planId);
    },
    []
  );

  // ===== PLANES DE SUSCRIPCIÓN =====
  const getAvailablePlans = useCallback(async (): Promise<
    ApiResponse<ISubscriptionPlan[]>
  > => {
    setLoading(true);
    setError(null);
    try {
      const response = await subscriptionService.getAvailablePlans();
      if (!response.success) {
        setError(getApiErrorMessage(response, 'Error al obtener planes de suscripción'));
      }
      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return {
        success: false,
        data: [] as ISubscriptionPlan[],
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const getPlanById = useCallback(
    async (id: string): Promise<ApiResponse<ISubscriptionPlan>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.getPlanById(id);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al obtener plan de suscripción'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: {} as ISubscriptionPlan,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createPlan = useCallback(
    async (
      plan: Omit<ISubscriptionPlan, 'id'>
    ): Promise<ApiResponse<ISubscriptionPlan>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.createPlan(plan);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al crear plan de suscripción'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: {} as ISubscriptionPlan,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updatePlan = useCallback(
    async (
      id: string,
      plan: Partial<ISubscriptionPlan>
    ): Promise<ApiResponse<ISubscriptionPlan>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.updatePlan(id, plan);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al actualizar plan de suscripción'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: {} as ISubscriptionPlan,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deletePlan = useCallback(
    async (id: string): Promise<ApiResponse<boolean>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.deletePlan(id);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al eliminar plan de suscripción'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: false,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getPlansByCategory = useCallback(
    async (category: string): Promise<ApiResponse<ISubscriptionPlan[]>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.getPlansByCategory(category);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al obtener planes por categoría'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: [] as ISubscriptionPlan[],
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ===== MÉTODOS DE PAGO =====
  const getPaymentMethods = useCallback(async (): Promise<
    ApiResponse<IPaymentMethod[]>
  > => {
    setLoading(true);
    setError(null);
    try {
      const response = await subscriptionService.getPaymentMethods();
      if (!response.success) {
        setError(getApiErrorMessage(response, 'Error al obtener métodos de pago'));
      }
      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return {
        success: false,
        data: [] as IPaymentMethod[],
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const getPaymentMethodById = useCallback(
    async (id: string): Promise<ApiResponse<IPaymentMethod>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.getPaymentMethodById(id);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al obtener método de pago'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: {} as IPaymentMethod,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createPaymentMethod = useCallback(
    async (
      paymentMethod: Omit<IPaymentMethod, 'id'>
    ): Promise<ApiResponse<IPaymentMethod>> => {
      setLoading(true);
      setError(null);
      try {
        const response =
          await subscriptionService.createPaymentMethod(paymentMethod);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al crear método de pago'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: {} as IPaymentMethod,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updatePaymentMethod = useCallback(
    async (
      id: string,
      paymentMethod: Partial<IPaymentMethod>
    ): Promise<ApiResponse<IPaymentMethod>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.updatePaymentMethod(
          id,
          paymentMethod
        );
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al actualizar método de pago'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: {} as IPaymentMethod,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deletePaymentMethod = useCallback(
    async (id: string): Promise<ApiResponse<boolean>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.deletePaymentMethod(id);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al eliminar método de pago'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: false,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const setDefaultPaymentMethod = useCallback(
    async (id: string): Promise<ApiResponse<IPaymentMethod>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await subscriptionService.setDefaultPaymentMethod(id);
        if (!response.success) {
          setError(getApiErrorMessage(response, 'Error al establecer método de pago predeterminado'));
        }
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return {
          success: false,
          data: {} as IPaymentMethod,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    // State
    loading,
    error,

    // Suscripciones
    getCurrentSubscription,
    getPaymentHistory,
    createSubscription,
    processPayment,
    cancelSubscription,
    reactivateSubscription,
    changePlan,

    // Planes de Suscripción
    getAvailablePlans,
    getPlanById,
    createPlan,
    updatePlan,
    deletePlan,
    getPlansByCategory,

    // Métodos de Pago
    getPaymentMethods,
    getPaymentMethodById,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    setDefaultPaymentMethod,
  };
}
